/**
 * The roster reducer's dispatch table — the one `switch` binding every leaf command type to its
 * handler in `./handlers/`, plus the public bus binding. Each case is a one-line delegation; the
 * per-command-family handler groups own the bodies, and `./handlers/context` owns the shared
 * helpers.
 *
 * The doctrine this tier is written to (the reducer is the wall; each case builds its own inverse;
 * reference-dependent rules read `ctx.compendium`) lives in `../reducer`, the facade this is
 * reached through.
 */

import { applyWithReducer, CommandError, err, type ApplyCtx, type LeafReducer, type Result } from "@lancer-vtt/shared";
import type { RosterState } from "../types";
import type { RosterCommand, RosterLeafCommand } from "../commands";
import type { RosterApplyResult, RosterEvent } from "./context";
import { applyCreatePilot, applyDeletePilot, applyRestorePilot } from "./pilotLifecycle";
import { applyRest, applySetLevel, applySetPilotBackground, applySetPilotBondmate, applySetPilotGearDamageType, applySetPilotHp, applySetPilotText } from "./pilotFields";
import { applySetCoreBonus, applySetLicenseRank, applySetMechSkill, applySetSkillTrigger, applySetTalentRank } from "./allocation";
import { applyAddPilotGear, applyRemovePilotGear } from "./gear";
import { applyCreateMech, applyDeleteMech, applyRenameMech, applyRestoreMech, applySetActiveMech, applySetMechImage, applySetMechNotes, applySetMechPrototype } from "./mechLifecycle";
import { applyRestoreLoadout, applySelectFrame } from "./frame";
import { applyEquipWeapon, applyUnequipWeapon } from "./weapons";
import { applyEquipSystem, applyUnequipSystem } from "./systems";
import { applyAttachMod, applyDetachMod } from "./mods";
import { applyRetrofitMount, applyUnRetrofitMount } from "./retrofit";
import { applyApplyPrintedMechState, applyFullRepair, applyFullRepairSquad, applyReprint, applyRestoreDestroyedMech, applyRestorePrintedMechState, applySpendRepairs } from "./repair";
import { applyRecordEncounterCommit } from "./encounterCommit";
import { applyApplyNpcWear, applyClearNpcWear } from "./npcWear";

// ── The leaf reducer ───────────────────────────────────────────────────────────

const rosterLeafReducer: LeafReducer<RosterState, RosterCommand, RosterEvent> = (
  state,
  command,
  ctx,
): Result<RosterApplyResult, CommandError> => {
  // `command` is already the non-batch leaf (the bus handles `batch`).
  const cmd = command as RosterLeafCommand;
  switch (cmd.type) {
    // ── pilot lifecycle ──────────────────────────────────────────────────────
    case "create_pilot":
      return applyCreatePilot(state, cmd);

    case "delete_pilot":
      return applyDeletePilot(state, cmd);

    case "restore_pilot":
      return applyRestorePilot(state, cmd);

    // ── pilot fields ─────────────────────────────────────────────────────────
    case "set_pilot_text":
      return applySetPilotText(state, cmd);

    case "set_pilot_background":
      return applySetPilotBackground(state, cmd);

    case "set_pilot_gear_damage_type":
      return applySetPilotGearDamageType(state, cmd);
    case "set_pilot_bondmate":
      return applySetPilotBondmate(state, cmd);

    case "set_pilot_hp":
      return applySetPilotHp(state, cmd);
    case "rest":
      return applyRest(state, cmd);

    case "set_level":
      return applySetLevel(state, cmd);

    case "set_mech_skill":
      return applySetMechSkill(state, cmd);

    case "set_skill_trigger":
      return applySetSkillTrigger(state, cmd);

    case "set_talent_rank":
      return applySetTalentRank(state, cmd, ctx);

    case "set_license_rank":
      return applySetLicenseRank(state, cmd);

    case "set_core_bonus":
      return applySetCoreBonus(state, cmd, ctx);

    case "add_pilot_gear":
      return applyAddPilotGear(state, cmd, ctx);

    case "remove_pilot_gear":
      return applyRemovePilotGear(state, cmd);

    // ── mech lifecycle ─────────────────────────────────────────────────────────
    case "create_mech":
      return applyCreateMech(state, cmd);

    case "delete_mech":
      return applyDeleteMech(state, cmd);

    case "restore_mech":
      return applyRestoreMech(state, cmd);

    case "rename_mech":
      return applyRenameMech(state, cmd);

    case "set_mech_image":
      return applySetMechImage(state, cmd);

    case "set_mech_notes":
      return applySetMechNotes(state, cmd);

    case "set_mech_prototype":
      return applySetMechPrototype(state, cmd);

    case "set_active_mech":
      return applySetActiveMech(state, cmd);

    // ── frame & weapons ─────────────────────────────────────────────────────────
    case "select_frame":
      return applySelectFrame(state, cmd, ctx);

    case "restore_loadout":
      return applyRestoreLoadout(state, cmd);

    case "equip_weapon":
      return applyEquipWeapon(state, cmd, ctx);

    case "unequip_weapon":
      return applyUnequipWeapon(state, cmd, ctx);

    case "equip_system":
      return applyEquipSystem(state, cmd, ctx);

    case "unequip_system":
      return applyUnequipSystem(state, cmd);

    case "attach_mod":
      return applyAttachMod(state, cmd, ctx);

    case "detach_mod":
      return applyDetachMod(state, cmd, ctx);

    case "retrofit_mount":
      return applyRetrofitMount(state, cmd, ctx);

    case "un_retrofit_mount":
      return applyUnRetrofitMount(state, cmd);

    // ── printed-mech state (between-encounter attrition + repair) ──────────────
    case "spend_repairs":
      return applySpendRepairs(state, cmd);

    case "full_repair":
      return applyFullRepair(state, cmd);
    case "full_repair_squad":
      return applyFullRepairSquad(state, cmd);

    case "apply_printed_mech_state":
      return applyApplyPrintedMechState(state, cmd);

    case "restore_printed_mech_state":
      return applyRestorePrintedMechState(state, cmd);

    case "restore_destroyed_mech":
      return applyRestoreDestroyedMech(state, cmd);

    case "reprint":
      return applyReprint(state, cmd);

    // ── encounter commit record ────────────────────────────────────────────────
    case "record_encounter_commit":
      return applyRecordEncounterCommit(state, cmd);

    // ── allied-NPC wear ledger ─────────────────────────────────────────────────
    case "apply_npc_wear":
      return applyApplyNpcWear(state, cmd);
    case "clear_npc_wear":
      return applyClearNpcWear(state, cmd);

    default:
      return err(
        new CommandError(
          `unknown roster command type: ${(cmd as { type?: unknown }).type}`,
        ),
      );
  }
};

// ── Public bus binding ─────────────────────────────────────────────────────────

/**
 * Apply a roster command through the generic command bus. A thin wrapper over
 * `applyWithReducer` bound to {@link rosterLeafReducer}, so the roster domain
 * gets atomic `batch`/inverse-composition/undo for free (roster is a separate state
 * domain on the *same* machinery). `ctx` carries the read-only
 * compendium for reference-dependent rules; omit it only for raw-only commands.
 */
export function applyRoster(
  state: RosterState,
  command: RosterCommand,
  ctx: ApplyCtx = {},
): Result<RosterApplyResult, CommandError> {
  return applyWithReducer({ reducer: rosterLeafReducer, state, command, ctx });
}
